/**
 * GCS Resumable Upload utility.
 *
 * Uploads a Blob to GCS using the resumable upload protocol:
 * - Sends 8 MB chunks via PUT with Content-Range headers
 * - GCS responds with 308 Resume Incomplete until the last chunk
 * - Retries on transient errors (5xx, 429) with exponential backoff
 * - Supports abort via AbortSignal
 * - Reports progress via callback
 *
 * Chunk size MUST be a multiple of 256 KB (GCS requirement).
 */

const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB
const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY_MS = 1000;

export interface GCSUploadProgress {
  uploaded: number;
  total: number;
  percentage: number;
}

/** Returns true for transient HTTP errors that should be retried. */
function isRetryable(status: number): boolean {
  return status === 429 || status === 500 || status === 502 || status === 503 || status === 504;
}

/** Exponential backoff with jitter. */
function backoffDelay(attempt: number): number {
  const base = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
  const jitter = Math.random() * 1000;
  return Math.min(base + jitter, 30_000);
}

async function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Upload aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

export async function uploadToGCSResumable(
  resumableUrl: string,
  file: Blob,
  totalSize: number,
  onProgress?: (progress: GCSUploadProgress) => void,
  abortSignal?: AbortSignal,
  chunkSize: number = DEFAULT_CHUNK_SIZE,
): Promise<void> {
  let offset = 0;

  while (offset < totalSize) {
    if (abortSignal?.aborted) {
      throw new DOMException("Upload aborted", "AbortError");
    }

    const end = Math.min(offset + chunkSize, totalSize);
    const chunk = file.slice(offset, end);

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(resumableUrl, {
          method: "PUT",
          headers: {
            "Content-Range": `bytes ${offset}-${end - 1}/${totalSize}`,
          },
          body: chunk,
          signal: abortSignal,
        });

        if (response.status === 308) {
          // Resume Incomplete — parse Range header for confirmed offset
          const range = response.headers.get("Range");
          if (range) {
            const match = range.match(/bytes=0-(\d+)/);
            offset = match ? parseInt(match[1], 10) + 1 : end;
          } else {
            offset = end;
          }
          lastError = null;
          break;
        } else if (response.ok) {
          // 200 or 201 — upload complete
          offset = totalSize;
          lastError = null;
          break;
        } else if (isRetryable(response.status) && attempt < MAX_RETRIES) {
          // Transient error — retry with backoff
          const retryAfter = response.headers.get("Retry-After");
          const waitMs = retryAfter
            ? parseInt(retryAfter, 10) * 1000
            : backoffDelay(attempt);
          console.warn(
            `GCS upload: ${response.status} at offset ${offset}, retrying in ${Math.round(waitMs)}ms (attempt ${attempt + 1}/${MAX_RETRIES})`,
          );
          await sleep(waitMs, abortSignal);
          continue;
        } else {
          const body = await response.text().catch(() => "");
          throw new Error(
            `GCS upload failed at offset ${offset}: ${response.status} ${response.statusText}. ${body}`,
          );
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          throw err;
        }
        // Network errors are retryable
        if (attempt < MAX_RETRIES) {
          console.warn(
            `GCS upload: network error at offset ${offset}, retrying (attempt ${attempt + 1}/${MAX_RETRIES})`,
            err,
          );
          await sleep(backoffDelay(attempt), abortSignal);
          lastError = err as Error;
          continue;
        }
        throw err;
      }
    }

    if (lastError) {
      throw lastError;
    }

    onProgress?.({
      uploaded: offset,
      total: totalSize,
      percentage: Math.round((offset / totalSize) * 100),
    });
  }
}

/**
 * Upload a file to the backend's local-upload endpoint (development only).
 *
 * Falls back to this when the backend returns storage_backend === "local"
 * (no GCS resumable URLs available).
 */
export async function uploadToLocalBackend(
  baseUrl: string,
  sessionId: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (progress: GCSUploadProgress) => void,
  abortSignal?: AbortSignal,
): Promise<void> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${baseUrl}/api/ontology/lakehouse/upload-session/${sessionId}/local-upload`,
    {
      method: "POST",
      headers, // Auth headers only — don't set Content-Type (browser sets multipart boundary)
      body: formData,
      signal: abortSignal,
    },
  );

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(
      `Local upload failed: ${response.status} ${response.statusText}. ${body}`,
    );
  }

  onProgress?.({
    uploaded: file.size,
    total: file.size,
    percentage: 100,
  });
}
